import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { adminService } from '../../services/adminService';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUseCases: 0,
    totalAITools: 0,
    totalPromptFields: 0,
    totalRelationships: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardStats = await adminService?.getStatistics();
      setStats({
        totalUseCases: dashboardStats?.useCases || 0,
        totalAITools: dashboardStats?.aiTools || 0,
        totalPromptFields: dashboardStats?.promptFields || 0,
        totalRelationships: 0 // Can be calculated if needed
      });
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Use Cases',
      value: stats?.totalUseCases,
      icon: 'Layers',
      color: 'bg-blue-500',
      description: 'Total categories'
    },
    {
      title: 'AI Tools',
      value: stats?.totalAITools,
      icon: 'Cpu',
      color: 'bg-purple-500',
      description: 'Available tools'
    },
    {
      title: 'Prompt Fields',
      value: stats?.totalPromptFields,
      icon: 'FileText',
      color: 'bg-green-500',
      description: 'Custom fields'
    }
  ];

  const quickActions = [
    {
      title: 'Manage Use Cases & AI Tools',
      description: 'Create, edit, and organize use cases and AI tools with prompt field configuration',
      icon: 'Settings',
      link: '/use-case-ai-tool-management',
      color: 'text-blue-600'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Manage use cases, AI tools, and prompt instruction relationships
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive mb-1">Error Loading Dashboard</h3>
                <p className="text-sm text-destructive/80">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadDashboardData}
                  className="mt-3"
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards?.map((card, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`${card?.color} rounded-lg p-3`}>
                  <Icon name={card?.icon} size={24} className="text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-1">{card?.value}</h3>
              <p className="text-sm font-medium text-foreground mb-1">{card?.title}</p>
              <p className="text-xs text-muted-foreground">{card?.description}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickActions?.map((action, index) => (
              <Link
                key={index}
                to={action?.link}
                className="bg-card border border-border rounded-lg p-6 hover:shadow-lg hover:border-primary transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <Icon name={action?.icon} size={32} className={action?.color} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {action?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{action?.description}</p>
                  </div>
                  <Icon name="ChevronRight" size={20} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* System Overview */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Info" size={20} className="text-primary" />
            System Overview
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Database Status</span>
              <span className="flex items-center gap-2 text-sm font-medium text-success">
                <div className="w-2 h-2 rounded-full bg-success"></div>
                Connected
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Total Entities</span>
              <span className="text-sm font-medium text-foreground">
                {stats?.totalUseCases + stats?.totalAITools + stats?.totalPromptFields}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">Last Updated</span>
              <span className="text-sm font-medium text-foreground">
                {new Date()?.toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;